import React, { useEffect, useState } from "react";
import api, { apiErrorMsg, formatINR } from "../lib/api";
import { useConfig } from "../lib/ConfigContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tag, Plus, Pencil, Trash2, Save, Star, Lock } from "lucide-react";
import { toast } from "sonner";
import PlanSectionsEditor from "../components/PlanSectionsEditor";

/**
 * Unified Plan Management (v2.18)
 * --------------------------------
 * Single admin tab that lists both client and dealer plans side-by-side.
 * Admin can add new plans, edit any plan (name/price/duration/features/toggles),
 * and delete custom (non-default) plans. Default plans cannot be deleted but
 * can be disabled via the "Enabled" switch.
 */

const blankForm = (role = "client") => ({
  name: "",
  role_type: role,
  price_inr: 999,
  duration_days: role === "client" ? 90 : 30,
  features: [],
  enabled: true,
  tagline: "",
  purpose: "",
  pitch: "",
  benefits: [],
  limitations: [],
  highlights: [],
  recommended: false,
});

export default function AdminUnifiedPlansTab() {
  const [plans, setPlans] = useState({ client: [], dealer: [] });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm("client"));
  const [featuresText, setFeaturesText] = useState("");
  const [benefitsText, setBenefitsText] = useState("");
  const [limitationsText, setLimitationsText] = useState("");
  const [highlightsText, setHighlightsText] = useState("");
  const { refresh } = useConfig();

  const load = async () => {
    try {
      const r = await api.get("/admin/unified-plans");
      setPlans(r.data);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to load plans"));
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = (role) => {
    setEditing(null);
    setForm(blankForm(role));
    setFeaturesText("");
    setBenefitsText("");
    setLimitationsText("");
    setHighlightsText("");
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...blankForm(p.role_type),
      ...p,
      features: p.features || [],
      benefits: p.benefits || [],
      limitations: p.limitations || [],
      highlights: p.highlights || [],
    });
    setFeaturesText((p.features || []).join("\n"));
    setBenefitsText((p.benefits || []).join("\n"));
    setLimitationsText((p.limitations || []).join("\n"));
    setHighlightsText((p.highlights || []).join("\n"));
    setShowForm(true);
  };

  const save = async () => {
    setBusy(true);
    const splitLines = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean);
    const features = splitLines(featuresText);
    const benefits = splitLines(benefitsText);
    const limitations = splitLines(limitationsText);
    const highlights = splitLines(highlightsText);
    const payload = {
      ...form,
      features,
      benefits,
      limitations,
      highlights,
      price_inr: Number(form.price_inr) || 0,
      duration_days: Number(form.duration_days) || 30,
    };
    try {
      if (editing?.id) {
        const { role_type, ...patch } = payload; // role_type immutable on edit
        await api.patch(`/admin/unified-plans/${editing.id}`, patch);
        toast.success("Plan updated");
      } else {
        await api.post("/admin/unified-plans", payload);
        toast.success("Plan created");
      }
      setShowForm(false);
      await load();
      refresh?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    }
    setBusy(false);
  };

  const remove = async (p) => {
    if (p.is_default) {
      toast.error("Default plans cannot be deleted. Disable via the Enabled toggle.");
      return;
    }
    if (!window.confirm(`Delete plan "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/unified-plans/${p.id}`);
      toast.success("Plan deleted");
      await load();
      refresh?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Delete failed"));
    }
  };

  const toggleEnabled = async (p) => {
    try {
      await api.patch(`/admin/unified-plans/${p.id}`, { enabled: !p.enabled });
      toast.success(p.enabled ? "Plan disabled" : "Plan enabled");
      await load();
      refresh?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Toggle failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-unified-plans-tab">
      <div className="flex items-center gap-2">
        <Tag size={16} className="text-[#0055FF]" />
        <h2 className="font-heading font-bold text-xl tracking-tight">Plan Management</h2>
        <span className="text-[11px] text-[#A1A1AA]">Single source of truth for client + dealer plans. Admin can add/edit/disable.</span>
      </div>

      {["client", "dealer"].map((role) => (
        <section key={role} data-testid={`plans-section-${role}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-lg capitalize">
              {role} Plans ({plans[role]?.length || 0})
            </h3>
            <Button onClick={() => openNew(role)} className="btn-brand rounded-sm" data-testid={`add-${role}-plan-btn`}>
              <Plus size={13} className="mr-1.5" /> Add {role} plan
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(plans[role] || []).map((p) => (
              <div
                key={p.id}
                className={`bg-white border rounded-sm p-4 ${p.enabled ? "border-[#E4E4E7]" : "border-[#FECACA] opacity-70"}`}
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <h4 className="font-heading font-semibold text-[16px] tracking-tight">{p.name}</h4>
                      {p.recommended && (
                        <span className="inline-flex items-center gap-0.5 bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[9px] uppercase tracking-wider font-semibold px-1.5 rounded-sm">
                          <Star size={9} /> Recommended
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-[#52525B]">
                      <span className="text-[#0055FF] font-mono-data font-semibold">{formatINR(p.price_inr)}</span>
                      {" · "}
                      {p.duration_days} days
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {p.is_default ? (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#A1A1AA]" title="Default plan — disable via toggle but cannot delete">
                        <Lock size={9} /> default
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold bg-[#EEF4FF] text-[#0044CC] px-1 rounded-sm">
                        custom
                      </span>
                    )}
                  </div>
                </div>

                {p.tagline && <div className="text-[11px] text-[#52525B] italic mb-2">{p.tagline}</div>}

                {(p.features || []).length > 0 && (
                  <ul className="text-[12px] text-[#52525B] space-y-1 mb-3">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex gap-1.5 before:content-['•'] before:text-[#0055FF]">{f}</li>
                    ))}
                    {p.features.length > 4 && (
                      <li className="text-[11px] text-[#A1A1AA]">+ {p.features.length - 4} more…</li>
                    )}
                  </ul>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-[#F4F4F5]">
                  <label className="flex items-center gap-1.5 text-[11px] text-[#52525B]">
                    <Switch checked={p.enabled} onCheckedChange={() => toggleEnabled(p)} data-testid={`plan-toggle-${p.id}`} />
                    {p.enabled ? "Enabled" : "Disabled"}
                  </label>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-[#DC2626]"
                      disabled={p.is_default}
                      onClick={() => remove(p)}
                      data-testid={`plan-delete-${p.id}`}
                      title={p.is_default ? "Default plans cannot be deleted" : "Delete"}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={(v) => !v && setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-sm" data-testid="plan-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">
              {editing?.id ? `Edit Plan: ${editing.name}` : `New ${form.role_type} plan`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* === Basics === */}
            <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Basics</div>
              <div>
                <Lbl>Name</Lbl>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="plan-name" placeholder="Premium" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Lbl>Role</Lbl>
                  <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v })} disabled={!!editing?.id}>
                    <SelectTrigger className="rounded-sm" data-testid="plan-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="dealer">Dealer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Lbl>Price (₹)</Lbl>
                  <Input type="number" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} className="rounded-sm" data-testid="plan-price" />
                </div>
                <div>
                  <Lbl>Duration (days)</Lbl>
                  <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className="rounded-sm" data-testid="plan-duration" />
                </div>
              </div>
            </div>

            {/* === Marketing copy === */}
            <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#52525B]">Marketing copy</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Lbl>Tagline</Lbl>
                  <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="rounded-sm" data-testid="plan-tagline" placeholder="The smart client's plan" />
                </div>
                <div>
                  <Lbl>Purpose</Lbl>
                  <Input value={form.purpose || ""} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="rounded-sm" data-testid="plan-purpose" placeholder="Avoid bad deals" />
                </div>
              </div>
              <div>
                <Lbl>Pitch (italic blockquote on card)</Lbl>
                <Textarea
                  rows={2}
                  value={form.pitch || ""}
                  onChange={(e) => setForm({ ...form, pitch: e.target.value })}
                  className="rounded-sm"
                  data-testid="plan-pitch"
                  placeholder="Stop wasting weekends visiting wrong properties — let our team curate for you."
                />
              </div>
            </div>

            {/* === v2.71.11 — Schema-driven sections editor === */}
            <PlanSectionsEditor planId={editing?.id || null} />

            {/* === Toggles === */}
            <div className="flex flex-wrap items-center gap-6 pt-1">
              <label className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="plan-enabled-switch" />
                <span className="text-sm font-semibold">Enabled</span>
              </label>
              <label className="flex items-center gap-2">
                <Switch checked={form.recommended} onCheckedChange={(v) => setForm({ ...form, recommended: v })} data-testid="plan-recommended-switch" />
                <span className="text-sm font-semibold inline-flex items-center gap-1">
                  <Star size={13} className="text-[#F59E0B]" /> Recommended
                </span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={busy} className="btn-brand rounded-sm" data-testid="plan-save-btn">
              <Save size={13} className="mr-1.5" /> {busy ? "Saving…" : "Save Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Lbl = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{children}</div>
);
